import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeGeneral.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import {
    Save,
    History,
    Image as ImageIcon,
    Plus,
    X,
    GripVertical,
    MapPin,
    Mail,
    Phone,
    Power,
    Check,
    AlertCircle,
    RefreshCw,
    LayoutTemplate
} from 'lucide-react'

const TEMPLATES = [
    { id: 'template1', label: 'Classic' },
    { id: 'template2', label: 'Modern' },
    { id: 'template3', label: 'Minimal' },
    { id: 'template4', label: 'Bold' },
    { id: 'template5', label: 'Elegant' }
]

const EMPTY_FORM = {
    storeName: '',
    storeTagline: '',
    storeDescription: '',
    storeImages: [],
    templateId: 'template1',
    storeEmail: '',
    storePhoneNumber: '',
    storeAddress: '',
    mapUrl: '',
    latitude: '',
    longitude: '',
    isActive: true
}

const MicrositeGeneral = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)
    const [newImageUrl, setNewImageUrl] = useState('')
    const [toast, setToast] = useState(null)
    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false)
    const [deactivationRemarks, setDeactivationRemarks] = useState('')

    const isDirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(original),
        [form, original]
    )

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadGeneral = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreGeneral')
        if (res?.status === 'success') {
            const r = res.response
            const next = {
                storeName: r.storeName || '',
                storeTagline: r.storeTagline || '',
                storeDescription: r.storeDescription || '',
                storeImages: Array.isArray(r.storeImages) ? r.storeImages : [],
                templateId: r.templateId || 'template1',
                storeEmail: r.storeEmail || '',
                storePhoneNumber: r.storePhoneNumber || '',
                storeAddress: r.storeAddress || '',
                mapUrl: r.mapUrl || '',
                latitude: r.latitude || '',
                longitude: r.longitude || '',
                isActive: !!r.isActive
            }
            setForm(next)
            setOriginal(next)
        } else {
            showToast('Could not load store details. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadGeneral()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const addImage = () => {
        const url = newImageUrl.trim()
        if (!url) return
        setForm((prev) => ({ ...prev, storeImages: [...prev.storeImages, url] }))
        setNewImageUrl('')
    }

    const removeImage = (idx) => {
        setForm((prev) => ({
            ...prev,
            storeImages: prev.storeImages.filter((_, i) => i !== idx)
        }))
    }

    const handleSave = async () => {
        setSaving(true)
        const res = await post('micrositeUpdateStoreGeneral', {
            storeName: form.storeName,
            storeTagline: form.storeTagline,
            storeDescription: form.storeDescription,
            storeImages: form.storeImages,
            templateId: form.templateId,
            storeEmail: form.storeEmail,
            storePhoneNumber: form.storePhoneNumber,
            storeAddress: form.storeAddress,
            mapUrl: form.mapUrl,
            latitude: form.latitude,
            longitude: form.longitude
        })
        setSaving(false)

        if (res?.status === 'success') {
            setOriginal(form)
            showToast('Changes saved successfully')
        } else {
            showToast('Could not save changes. Please try again.', 'error')
        }
    }

    const handleToggleActive = async () => {
        if (form.isActive) {
            setDeactivateModalOpen(true)
            return
        }
        const res = await post('micrositeToggleStoreActive', { isActive: true })
        if (res?.status === 'success') {
            setForm((prev) => ({ ...prev, isActive: true }))
            setOriginal((prev) => ({ ...prev, isActive: true }))
            showToast('Store activated')
        } else {
            showToast('Could not activate store', 'error')
        }
    }

    const confirmDeactivate = async () => {
        const res = await post('micrositeToggleStoreActive', {
            isActive: false,
            deactivationRemarks
        })
        if (res?.status === 'success') {
            setForm((prev) => ({ ...prev, isActive: false }))
            setOriginal((prev) => ({ ...prev, isActive: false }))
            showToast('Store deactivated')
        } else {
            showToast('Could not deactivate store', 'error')
        }
        setDeactivateModalOpen(false)
        setDeactivationRemarks('')
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading store details…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.pageTitle}>General</h1>
                    <p className={styles.pageSubtitle}>
                        Your salon's name, story, and contact details — shown across your microsite.
                    </p>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.historyBtn}>
                        <History size={14} />
                        History
                    </button>
                    <button
                        type="button"
                        className={styles.saveBtn}
                        onClick={handleSave}
                        disabled={!isDirty || saving}
                    >
                        {isDirty && <span className={styles.unsavedDotSmall} />}
                        <Save size={14} />
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>

            {/* Identity */}
            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Identity</h2>
                <p className={styles.groupDesc}>How your salon introduces itself to visitors.</p>

                <label className={styles.label}>Store name</label>
                <input
                    type="text"
                    className={styles.input}
                    value={form.storeName}
                    onChange={(e) => updateField('storeName', e.target.value)}
                    placeholder="e.g. Glam Studio by Priya"
                />

                <label className={styles.label}>Tagline</label>
                <input
                    type="text"
                    className={styles.input}
                    value={form.storeTagline}
                    onChange={(e) => updateField('storeTagline', e.target.value)}
                    placeholder="A short catchy phrase, under 100 characters"
                    maxLength={100}
                />
                <div className={styles.charCount}>{form.storeTagline.length}/100</div>

                <label className={styles.label}>Description</label>
                <textarea
                    className={styles.textarea}
                    rows={4}
                    value={form.storeDescription}
                    onChange={(e) => updateField('storeDescription', e.target.value)}
                    placeholder="Tell visitors what makes your salon special…"
                />
            </section>

            {/* Images */}
            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Gallery images</h2>
                <p className={styles.groupDesc}>Shown in your hero banner and photo gallery.</p>

                <div className={styles.imageGrid}>
                    {form.storeImages.map((url, idx) => (
                        <div key={idx} className={styles.imageTile}>
                            <GripVertical size={14} className={styles.dragHandle} />
                            <img src={url} alt={`Gallery ${idx + 1}`} className={styles.imageThumb} onError={(e) => { e.currentTarget.style.display = 'none' }} />
                            <span className={styles.imageUrlText} title={url}>{url}</span>
                            <button type="button" className={styles.imageRemoveBtn} onClick={() => removeImage(idx)}>
                                <X size={13} />
                            </button>
                        </div>
                    ))}
                </div>

                <div className={styles.addImageRow}>
                    <ImageIcon size={14} className={styles.addImageIcon} />
                    <input
                        type="text"
                        className={styles.input}
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addImage()}
                        placeholder="Paste an image URL and press Enter"
                    />
                    <button type="button" className={styles.addBtn} onClick={addImage}>
                        <Plus size={14} />
                        Add
                    </button>
                </div>
            </section>

            {/* Template */}
            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Template</h2>
                <p className={styles.groupDesc}>Choose the overall layout style for your microsite.</p>

                <div className={styles.templateGrid}>
                    {TEMPLATES.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            className={`${styles.templateCard} ${form.templateId === t.id ? styles.templateCardActive : ''}`}
                            onClick={() => updateField('templateId', t.id)}
                        >
                            <LayoutTemplate size={20} />
                            <span>{t.label}</span>
                            {form.templateId === t.id && <Check size={13} className={styles.templateCheck} />}
                        </button>
                    ))}
                </div>
            </section>

            {/* Contact */}
            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Contact details</h2>
                <p className={styles.groupDesc}>Shown in your footer and contact page.</p>

                <div className={styles.row2}>
                    <div>
                        <label className={styles.label}><Mail size={12} /> Email</label>
                        <input
                            type="email"
                            className={styles.input}
                            value={form.storeEmail}
                            onChange={(e) => updateField('storeEmail', e.target.value)}
                            placeholder="info@yoursalon.com"
                        />
                    </div>
                    <div>
                        <label className={styles.label}><Phone size={12} /> Phone number</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={form.storePhoneNumber}
                            onChange={(e) => updateField('storePhoneNumber', e.target.value)}
                            placeholder="9999999999"
                        />
                    </div>
                </div>

                <label className={styles.label}><MapPin size={12} /> Address</label>
                <textarea
                    className={styles.textarea}
                    rows={2}
                    value={form.storeAddress}
                    onChange={(e) => updateField('storeAddress', e.target.value)}
                    placeholder="Full physical address"
                />

                <label className={styles.label}>Google Maps embed URL</label>
                <input
                    type="text"
                    className={styles.input}
                    value={form.mapUrl}
                    onChange={(e) => updateField('mapUrl', e.target.value)}
                    placeholder="https://www.google.com/maps/embed?..."
                />

                <div className={styles.row2}>
                    <div>
                        <label className={styles.label}>Latitude</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={form.latitude}
                            onChange={(e) => updateField('latitude', e.target.value)}
                            placeholder="28.612912"
                        />
                    </div>
                    <div>
                        <label className={styles.label}>Longitude</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={form.longitude}
                            onChange={(e) => updateField('longitude', e.target.value)}
                            placeholder="77.376944"
                        />
                    </div>
                </div>
            </section>

            {/* Store status */}
            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Store status</h2>
                <div className={styles.statusToggleRow}>
                    <div>
                        <div className={styles.statusToggleLabel}>
                            {form.isActive ? 'Your microsite is active' : 'Your microsite is deactivated'}
                        </div>
                        <div className={styles.groupDesc}>
                            {form.isActive
                                ? 'Visitors can view your site at your published domain.'
                                : 'Your site is hidden from visitors until reactivated.'}
                        </div>
                    </div>
                    <button
                        type="button"
                        className={`${styles.toggleBtn} ${form.isActive ? styles.toggleBtnActive : ''}`}
                        onClick={handleToggleActive}
                    >
                        <Power size={13} />
                        {form.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                </div>
            </section>

            {/* Deactivate confirmation modal */}
            {deactivateModalOpen && (
                <div className={styles.modalOverlay} onClick={() => setDeactivateModalOpen(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 className={styles.modalTitle}>Deactivate your microsite?</h3>
                        <p className={styles.modalDesc}>
                            Visitors won't be able to view your site until you reactivate it.
                        </p>
                        <label className={styles.label}>Reason (optional)</label>
                        <textarea
                            className={styles.textarea}
                            rows={2}
                            value={deactivationRemarks}
                            onChange={(e) => setDeactivationRemarks(e.target.value)}
                            placeholder="e.g. Closed for renovation"
                        />
                        <div className={styles.modalActions}>
                            <button type="button" className={styles.modalCancelBtn} onClick={() => setDeactivateModalOpen(false)}>
                                Cancel
                            </button>
                            <button type="button" className={styles.modalConfirmBtn} onClick={confirmDeactivate}>
                                Deactivate
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MicrositeGeneral